import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { MapPin, Globe, Phone, Star, Heart, ChevronLeft, CheckCircle, AtSign, Users, Clock } from 'lucide-react';
import { api } from '../context/AuthContext';
import { useAuth } from '../context/AuthContext';
import { useSEO } from '../hooks/useSEO';
import ProductCard from '../components/ProductCard';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const ProducerDetailPage = () => {
  const { slug } = useParams();
  const { isAuthenticated } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isFavorite, setIsFavorite] = useState(false);

  useEffect(() => {
    const fetchProducer = async () => {
      try {
        const res = await api.get(`/producers/${slug}`);
        setData(res.data);
      } catch (error) {
        console.error('Error fetching producer:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducer();
  }, [slug]);

  const handleToggleFavorite = async () => {
    if (!isAuthenticated) {
      toast.error('Accedi per aggiungere ai preferiti');
      return;
    }
    if (data?.producer) {
      try {
        if (isFavorite) {
          await api.delete(`/favorites/${data.producer.id}`);
          setIsFavorite(false);
          toast.success('Rimosso dai preferiti');
        } else {
          await api.post(`/favorites/${data.producer.id}`);
          setIsFavorite(true);
          toast.success('Aggiunto ai preferiti');
        }
      } catch (error) {
        toast.error('Errore nell\'aggiornamento dei preferiti');
      }
    }
  };

  const _producer = data?.producer;
  const _region = data?.region;
  const _SITE_URL = (process.env.REACT_APP_SITE_URL || '').replace(/\/$/, '');
  useSEO({
    title: _producer?.name,
    description: _producer?.description
      ? _producer.description.slice(0, 155)
      : _producer
        ? `Scopri ${_producer.name}, produttore del Sud Italia${_region ? ` in ${_region.name}` : ''}. Acquista i loro prodotti artigianali direttamente online.`
        : undefined,
    image: _producer?.cover_url || _producer?.logo_url || undefined,
    canonicalPath: _producer ? `/produttori/${_producer.slug}` : undefined,
    jsonLd: _producer ? {
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      '@id': `${_SITE_URL}/produttori/${_producer.slug}`,
      name: _producer.name,
      description: _producer.description || undefined,
      image: _producer.logo_url || undefined,
      url: _producer.website || `${_SITE_URL}/produttori/${_producer.slug}`,
      telephone: _producer.phone || undefined,
      address: _producer.address ? {
        '@type': 'PostalAddress',
        streetAddress: _producer.address,
        addressLocality: _producer.city || undefined,
        postalCode: _producer.postal_code || undefined,
        addressCountry: 'IT',
      } : undefined,
      sameAs: [_producer.instagram, _producer.facebook, _producer.website].filter(Boolean),
      ...(data?.average_rating && data?.review_count > 0 ? {
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: data.average_rating,
          reviewCount: data.review_count,
          bestRating: 5,
          worstRating: 1,
        },
      } : {}),
    } : undefined,
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="h-64 bg-gray-200 animate-pulse" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-200 rounded w-1/3" />
            <div className="h-4 bg-gray-200 rounded w-1/2" />
          </div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-text-primary">Produttore non trovato</h1>
          <Button asChild className="mt-4">
            <Link to="/produttori">Torna ai produttori</Link>
          </Button>
        </div>
      </div>
    );
  }

  const { producer, region, products, average_rating, review_count } = data;

  return (
    <div className="min-h-screen bg-background" data-testid="producer-detail-page">
      {/* Cover Image */}
      <div className="relative h-48 md:h-64 lg:h-80 overflow-hidden">
        <img
          src={producer.cover_url || 'https://images.pexels.com/photos/2927972/pexels-photo-2927972.jpeg'}
          alt={producer.name}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
        
        {/* Back Button */}
        <Link 
          to="/produttori" 
          className="absolute top-4 left-4 flex items-center gap-1 text-white bg-black/30 hover:bg-black/50 px-3 py-1.5 rounded-lg transition-colors"
        >
          <ChevronLeft className="w-4 h-4" />
          Produttori
        </Link>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Profile Header */}
        <div className="relative -mt-16 mb-8">
          <div className="bg-white rounded-xl p-6 shadow-lg">
            <div className="flex flex-col md:flex-row md:items-end gap-6">
              {/* Logo */}
              <div className="flex-shrink-0 -mt-20 md:-mt-24">
                <img
                  src={producer.logo_url || 'https://images.pexels.com/photos/15899638/pexels-photo-15899638.jpeg'}
                  alt={producer.name}
                  className="w-28 h-28 md:w-36 md:h-36 rounded-xl object-cover border-4 border-white shadow-lg"
                />
              </div>

              {/* Info */}
              <div className="flex-1">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <h1 className="font-heading text-2xl md:text-3xl font-bold text-text-primary">
                        {producer.name}
                      </h1>
                      {producer.is_featured && (
                        <span className="inline-flex items-center gap-1 bg-secondary/10 text-secondary px-2.5 py-0.5 rounded-full text-xs font-semibold">
                          <CheckCircle className="w-3 h-3" />
                          Verificato
                        </span>
                      )}
                    </div>
                    
                    <div className="flex flex-wrap items-center gap-4 text-sm text-text-secondary">
                      {region && (
                        <span className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          {region.name}
                        </span>
                      )}
                      {average_rating && (
                        <span className="flex items-center gap-1">
                          <Star className="w-4 h-4 fill-accent text-accent" />
                          {average_rating.toFixed(1)} ({review_count} recensioni)
                        </span>
                      )}
                    </div>
                  </div>

                  <Button
                    variant="outline"
                    onClick={handleToggleFavorite}
                    className={`hidden md:flex ${isFavorite ? 'text-red-500 border-red-500' : ''}`}
                    data-testid="favorite-producer-btn"
                  >
                    <Heart className={`w-5 h-5 mr-2 ${isFavorite ? 'fill-current' : ''}`} />
                    {isFavorite ? 'Nei Preferiti' : 'Aggiungi ai Preferiti'}
                  </Button>
                </div>
              </div>
            </div>

            {/* Mobile Favorite Button */}
            <Button
              variant="outline"
              onClick={handleToggleFavorite}
              className={`md:hidden w-full mt-4 ${isFavorite ? 'text-red-500 border-red-500' : ''}`}
            >
              <Heart className={`w-5 h-5 mr-2 ${isFavorite ? 'fill-current' : ''}`} />
              {isFavorite ? 'Nei Preferiti' : 'Aggiungi ai Preferiti'}
            </Button>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8 pb-16">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Description */}
            {producer.description && (
              <div>
                <h2 className="font-heading text-xl font-bold mb-4">Chi Siamo</h2>
                <p className="text-text-secondary whitespace-pre-line">{producer.description}</p>
              </div>
            )}

            {/* Story */}
            {producer.story && (
              <div className="bg-white p-6 rounded-xl border border-border">
                <h2 className="font-heading text-xl font-bold mb-4">La Nostra Storia</h2>
                <p className="text-text-secondary whitespace-pre-line">{producer.story}</p>
              </div>
            )}

            {/* Products */}
            <div>
              <h2 className="font-heading text-xl font-bold mb-4">
                I Nostri Prodotti ({products?.length || 0})
              </h2>
              {products && products.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {products.map(product => (
                    <ProductCard key={product.id} product={product} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 bg-white rounded-xl border border-border">
                  <p className="text-text-secondary">Nessun prodotto disponibile al momento</p>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white p-6 rounded-xl border border-border sticky top-24 space-y-4">
              <h3 className="font-heading font-semibold">Informazioni</h3>
              
              {producer.website && (
                <a 
                  href={producer.website} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 text-text-secondary hover:text-primary transition-colors"
                >
                  <Globe className="w-5 h-5" />
                  <span className="truncate">{producer.website.replace(/^https?:\/\//, '')}</span>
                </a>
              )}
              
              {producer.phone && (
                <a 
                  href={`tel:${producer.phone}`}
                  className="flex items-center gap-3 text-text-secondary hover:text-primary transition-colors"
                >
                  <Phone className="w-5 h-5" />
                  <span>{producer.phone}</span>
                </a>
              )}
              
              {producer.address && (
                <div className="flex items-start gap-3 text-text-secondary">
                  <MapPin className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <span>{[producer.address, producer.city, producer.postal_code].filter(Boolean).join(', ')}</span>
                </div>
              )}

              {producer.instagram && (
                <a
                  href={producer.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 text-text-secondary hover:text-primary transition-colors"
                >
                  <AtSign className="w-5 h-5" />
                  <span className="truncate">Instagram</span>
                </a>
              )}

              {producer.facebook && (
                <a
                  href={producer.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 text-text-secondary hover:text-primary transition-colors"
                >
                  <Users className="w-5 h-5" />
                  <span className="truncate">Facebook</span>
                </a>
              )}

              {producer.business_hours && (
                <div className="flex items-start gap-3 text-text-secondary">
                  <Clock className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <pre className="text-sm whitespace-pre-wrap font-sans">{producer.business_hours}</pre>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProducerDetailPage;
